import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ShieldCheck, Sparkles, IndianRupee, MessageCircle, Camera, Star,
  ArrowRight, CheckCircle2, Phone, Users, Quote, Heart, Activity, Briefcase,
  Gift, MapPin, Compass, BedDouble,
} from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import PoojaCard from "@/components/PoojaCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { getPoojas, getTestimonials, getFaqs, getGuidanceList, formatPrice, resolveImg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { waLink, generalWaMessage, astrologerWaMessage } from "@/lib/whatsapp";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ICONS = { Heart, Activity, Briefcase, Sparkles };

const TRUST = [
  { icon: ShieldCheck, title: "Main Temple Priests", text: "Hereditary pandits connected to Gokarna Mahabaleshwar Temple." },
  { icon: IndianRupee, title: "Transparent Pricing", text: "Clear, upfront pricing with no hidden charges or middlemen." },
  { icon: MessageCircle, title: "Quick WhatsApp Help", text: "Honest guidance before you book — we never pressure you." },
  { icon: Sparkles, title: "Authentic Vedic Rituals", text: "Complete procedure, real samagri, genuine spiritual benefit." },
];

const Home = () => {
  const site = useSite();
  const { data: featured = [] } = useQuery({ queryKey: ["poojas", "featured"], queryFn: () => getPoojas({ featured: true }) });
  const { data: allPoojas = [] } = useQuery({ queryKey: ["poojas", "all"], queryFn: () => getPoojas() });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: getTestimonials });
  const { data: faqs = [] } = useQuery({ queryKey: ["faqs", "general"], queryFn: () => getFaqs("general") });
  const { data: guidance = [] } = useQuery({ queryKey: ["guidance"], queryFn: getGuidanceList });

  const heroImg = "https://images.pexels.com/photos/38122489/pexels-photo-38122489.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
  const gallery = [...new Set(allPoojas.flatMap((p) => p.images || []).filter(Boolean))].slice(0, 6);
  const orgJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Shrigokarnam",
    description: site.tagline,
    address: { "@type": "PostalAddress", addressLocality: "Gokarna", addressRegion: "Karnataka", addressCountry: "IN" },
  }), [site.tagline]);

  return (
    <Layout>
      <Seo
        title="Shrigokarnam | Authentic Pooja Booking at Gokarna Mahabaleshwar Temple"
        description="Book authentic Vedic poojas at Gokarna with our main priest team. Transparent pricing, honest guidance, quick WhatsApp booking. Rudrabhishek, Rahu Ketu, Mangal Dosha & more."
        jsonLd={orgJsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center">
        <img src={heroImg} alt="Gokarna temple" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2C1E16]/90 via-[#2C1E16]/70 to-[#2C1E16]/30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20">
          <div className="max-w-2xl reveal">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary/90 border border-gold/40 px-4 py-1.5 text-xs tracking-[0.2em] uppercase font-semibold text-gold shadow-lg">
              <Sparkles className="h-3.5 w-3.5" /> Dakshina Kashi · Gokarna
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.05] tracking-tight">
              {site.hero_title || "Trusted Poojas at Gokarna, by Our Main Temple Priest Team"}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-white/85 max-w-xl">
              {site.hero_subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <EnquiryDialog
                trigger={
                  <button data-testid="hero-enquire-button" className="inline-flex items-center gap-2 rounded-md bg-primary px-7 py-3.5 font-semibold text-white hover:bg-[#CC5204] transition-colors">
                    Enquire Now <ArrowRight className="h-4 w-4" />
                  </button>
                }
              />
              <a href={waLink(site.whatsapp_number, generalWaMessage)} target="_blank" rel="nofollow noopener noreferrer" data-testid="hero-whatsapp-button" className="inline-flex items-center gap-2 rounded-md bg-whatsapp px-7 py-3.5 font-semibold text-white hover:opacity-90 transition-opacity">
                <Phone className="h-4 w-4" /> Book on WhatsApp
              </a>
              <Link to="/poojas" data-testid="hero-view-poojas-button" className="inline-flex items-center gap-2 rounded-md border-2 border-gold px-7 py-3.5 font-semibold text-white hover:bg-gold/15 transition-colors">
                View Poojas
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/75">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-gold" /> No middlemen</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-gold" /> Honest guidance</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-gold" /> Real photos & updates</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-secondary">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {TRUST.map((t) => (
            <div key={t.title} className="bg-secondary p-6 text-white">
              <t.icon className="h-7 w-7 text-gold" />
              <h3 className="mt-3 font-heading text-xl text-white">{t.title}</h3>
              <p className="mt-1 text-sm text-white/70 leading-relaxed">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FREE ASTROLOGY + YATRA ASSISTANCE */}
      <section className="bg-gradient-to-br from-[#6B1A22] to-[#4A0F16] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-10 justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-gold text-secondary px-3.5 py-1 text-xs font-bold uppercase tracking-[0.15em]">
                <Gift className="h-3.5 w-3.5" /> Included Free with Every Booking
              </span>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl text-white tracking-tight leading-tight">
                Book Any Pooja — Get Free Astrologer Consultation & Complete Yatra Assistance
              </h2>
              <p className="mt-4 text-white/80 leading-relaxed">
                Confused about which pooja you truly need? Chat with our astrologer at <span className="text-gold font-semibold">no extra cost</span> when you book with us. We also take care of your darshan, stay and travel guidance — so your entire Gokarna yatra is handled in one trusted place.
              </p>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: Sparkles, label: "Free Astrology Guidance" },
                  { icon: MapPin, label: "Darshan Assistance" },
                  { icon: BedDouble, label: "Stay & Travel Help" },
                ].map((it) => (
                  <div key={it.label} className="flex items-center gap-3 rounded-lg bg-white/10 px-4 py-3">
                    <it.icon className="h-5 w-5 text-gold shrink-0" />
                    <span className="text-sm font-medium text-white">{it.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <a
              href={waLink(site.whatsapp_number, astrologerWaMessage)}
              target="_blank"
              rel="nofollow noopener noreferrer"
              data-testid="astrologer-chat-button"
              className="shrink-0 inline-flex items-center gap-2 rounded-md bg-gold px-7 py-4 font-semibold text-secondary hover:bg-[#c49f2e] transition-colors"
            >
              <MessageCircle className="h-5 w-5" /> Chat with our Astrologer
            </a>
          </div>
        </div>
      </section>

      {/* FEATURED POOJAS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-primary">Featured Poojas</span>
            <h2 className="mt-2 font-heading text-4xl text-secondary tracking-tight">Most Requested Rituals at Gokarna</h2>
          </div>
          <Link to="/poojas" className="inline-flex items-center gap-1.5 text-secondary font-medium hover:text-primary transition-colors" data-testid="view-all-poojas-link">
            View all poojas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {featured.map((p, i) => <PoojaCard key={p.id} pooja={p} index={i} />)}
        </div>
      </section>

      {/* WHICH POOJA GUIDANCE */}
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-primary">Confused about which pooja?</span>
            <h2 className="mt-2 font-heading text-4xl text-secondary tracking-tight">Which Pooja is Right for Me?</h2>
            <p className="mt-3 text-muted-foreground">Tell us your problem and we'll honestly guide you to the right ritual — no fear, no pressure.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {guidance.map((g, i) => {
              const Icon = ICONS[g.icon] || Sparkles;
              return (
                <Link key={g.id} to={`/guidance/${g.slug}`} data-testid={`guidance-card-${g.slug}`} className="reveal group bg-white border border-border rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all" style={{ animationDelay: `${i * 0.07}s` }}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 font-heading text-xl text-secondary leading-tight">{g.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{g.problem_description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">Get guidance <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRANSPARENT PRICING */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20 md:py-24">
        <div className="grid lg:grid-cols-3 gap-10">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-primary">Transparent Pricing</span>
            <h2 className="mt-2 font-heading text-4xl text-secondary tracking-tight">No Hidden Costs. Ever.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">Every pooja shows a clear starting price. Final cost depends only on the scale of the ritual and items included — confirmed upfront before you book.</p>
            <EnquiryDialog trigger={<button data-testid="pricing-enquire-button" className="mt-6 inline-flex items-center gap-2 rounded-md bg-secondary px-6 py-3 font-semibold text-white hover:bg-[#52131A] transition-colors">Ask for a quote <ArrowRight className="h-4 w-4" /></button>} />
          </div>
          <div className="lg:col-span-2 bg-white border border-border rounded-xl overflow-hidden">
            <div className="divide-y divide-border">
              {allPoojas.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/poojas/${p.slug}`} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-muted/50 transition-colors" data-testid={`pricing-row-${p.slug}`}>
                  <div>
                    <h4 className="font-heading text-xl text-secondary">{p.name}</h4>
                    <p className="text-xs text-muted-foreground">{p.suitable_for}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="block text-[11px] text-muted-foreground">from</span>
                    <span className="flex items-center text-lg font-semibold text-primary"><IndianRupee className="h-4 w-4" />{p.price?.toLocaleString("en-IN")}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="bg-cream">
          <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20">
            <div className="flex items-center gap-2 mb-8">
              <Camera className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-4xl text-secondary tracking-tight">Real Pooja Moments</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {gallery.map((img, i) => (
                <div key={img} className={`reveal overflow-hidden rounded-xl ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`} style={{ animationDelay: `${i * 0.06}s` }}>
                  <img src={resolveImg(img)} alt="Pooja at Gokarna" loading="lazy" className="h-full w-full object-cover aspect-[4/3] hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-primary">Devotee Voices</span>
          <h2 className="mt-2 font-heading text-4xl text-secondary tracking-tight">Trusted by Families Across India</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.slice(0, 6).map((t, i) => (
            <div key={t.id} className="reveal bg-white border border-border rounded-xl p-7" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`testimonial-${i}`}>
              <Quote className="h-7 w-7 text-gold" />
              <p className="mt-3 text-muted-foreground leading-relaxed italic">"{t.text}"</p>
              <div className="mt-5 flex items-center gap-1 text-gold">
                {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-3 font-heading text-xl text-secondary">{t.name}</p>
              <p className="text-xs text-muted-foreground">{t.location}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="bg-cream">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 py-20">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-primary">Common Questions</span>
            <h2 className="mt-2 font-heading text-4xl text-secondary tracking-tight">Before You Book</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="home-faq-accordion">
            {faqs.slice(0, 5).map((f) => (
              <AccordionItem key={f.id} value={f.id} className="bg-white border border-border rounded-xl px-5">
                <AccordionTrigger className="text-left font-medium text-secondary hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link to="/faq" className="inline-flex items-center gap-1.5 text-secondary font-medium hover:text-primary" data-testid="view-all-faqs-link">All FAQs <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-secondary text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h2 className="font-heading text-4xl text-white tracking-tight">Ready to book your pooja?</h2>
            <p className="mt-2 text-white/75 max-w-xl">Talk to our priest team today. Honest guidance, transparent pricing, quick response.</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={waLink(site.whatsapp_number, generalWaMessage)} target="_blank" rel="nofollow noopener noreferrer" data-testid="cta-whatsapp-button" className="inline-flex items-center gap-2 rounded-md bg-whatsapp px-7 py-3.5 font-semibold text-white hover:opacity-90"><Phone className="h-4 w-4" /> WhatsApp Us</a>
            <Link to="/contact" data-testid="cta-contact-button" className="inline-flex items-center gap-2 rounded-md bg-primary px-7 py-3.5 font-semibold text-white hover:bg-[#CC5204]">Send Enquiry</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
